"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { ActivityCard } from "../../components/activities/ActivityCard";
import { ArticleCardCompact } from "../../components/activities/ArticleCardCompact";
import { DocumentCard } from "../../components/activities/DocumentCard";
import { StatCard } from "../../components/activities/StatCard";
import { ProductCard, type ProductCardItem } from "../../components/activities/ProductCard";
import type { PaginatedActivityGridComponent, PaginatedActivityGridItem } from "../../types";
import { RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import type { UiStringKey } from "../../i18n/strings";
import { translateCategoryPathLabel } from "../../i18n/categories";
import { resolveLinkHref } from "../../utils/links";
import { LinkTileGridRenderer } from "./LinkTileGridRenderer";

const DEFAULT_ITEMS_PER_PAGE = 24;

type SortKey = "date-desc" | "date-asc" | "title-asc";

// Module-level, so the options carry locale-independent KEYS — the visible
// label is resolved with t(locale, …) at render time where the locale lives.
const SORT_OPTIONS: { value: SortKey; labelKey: UiStringKey }[] = [
  { value: "date-desc", labelKey: "sortNewest" },
  { value: "date-asc", labelKey: "sortOldest" },
  { value: "title-asc", labelKey: "sortAlpha" },
];

/**
 * Paginated activity grid renderer — server-side first.
 *
 * Every filter (q, sort, year, taxonomy, category) and the page number live
 * in the URL. The Next.js page layer (`applyPaginatedActivityGridSearchParams`)
 * reads them, filters + slices the in-payload set on the server, then hands
 * us the already-correct items + pagination snapshot. The renderer renders
 * that snapshot verbatim and emits new URLs for navigation — it does NOT
 * re-filter or re-slice locally, which would either double-filter or render
 * empty pages when the server already sliced the data.
 *
 * The form fields hold *draft* state so the user can change several inputs
 * before hitting Filtrer / Enter. Pagination is shown as classic anchor
 * links (RuntimeLink → Next.js Link), so the URL always reflects the page
 * the user is on and `/publications/notes-de-conjoncture?page=3` is a
 * shareable, server-rendered URL.
 */
export function PaginatedActivityGridRenderer({ component }: { component: PaginatedActivityGridComponent }) {
  const locale = useLocale();
  const content = component.content ?? { items: [] };
  const items: PaginatedActivityGridItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination ?? {};
  const itemsPerPage =
    typeof pagination.itemsPerPage === "number" && pagination.itemsPerPage > 0
      ? pagination.itemsPerPage
      : DEFAULT_ITEMS_PER_PAGE;

  // ----- Server-applied filter state (the SOURCE OF TRUTH). -----
  const appliedQuery = (pagination.query ?? "").toString();
  const appliedTaxonomy = (pagination.taxonomyId ?? "").toString();
  const appliedCategory = (pagination.categoryId ?? "").toString();
  // The SSR helper exposes `year` and `sort` via the pagination metadata when
  // it handles those params. They're optional so older payloads still work.
  const appliedYear = ((pagination as { year?: string }).year ?? "").toString();
  const rawSort = ((pagination as { sort?: string }).sort ?? "").toString();
  const appliedSort: SortKey = isSortKey(rawSort) ? rawSort : "date-desc";

  const taxonomies = Array.isArray(pagination.taxonomies) ? pagination.taxonomies : [];
  const categories = Array.isArray(pagination.categories) ? pagination.categories : [];
  const enableTaxonomyFilter = content.enableTaxonomyFilter !== false && taxonomies.length > 0;
  const enableCategoryFilter = content.enableCategoryFilter !== false && categories.length > 0;
  // Available years can come from the SSR helper (preferred — it sees the
  // full unsliced set) or be derived from the current page's items as a
  // graceful fallback for payloads pre-dating the helper change.
  const serverYears = Array.isArray((pagination as { years?: unknown }).years)
    ? ((pagination as { years: unknown[] }).years.filter((y) => typeof y === "string") as string[])
    : [];
  const fallbackYears = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      const year = extractYear(item.meta);
      if (year) set.add(year);
    }
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [items]);
  const availableYears = serverYears.length > 0
    ? [...serverYears].sort((a, b) => b.localeCompare(a))
    : fallbackYears;

  const searchPlaceholder = (content.searchPlaceholder ?? t(locale, "search")).toString();
  // Showcase mode: home "Produits en vedette" / "Guides & actualités" use the
  // grid purely to display a curated row — no search/sort/year bar, no result
  // count, no pagination. Defaults to a full controlled index everywhere else.
  const showControls = (content as { showControls?: boolean }).showControls !== false;
  const cardType = (content.cardType ?? "ActivityGrid").toString();
  const isDocumentCard = cardType === "document";
  // News-website article cards: same look as the "À lire aussi" footer on
  // article pages (ArticleCardCompact). Picks up `title`, `description`
  // (excerpt), `author` (byline) and `meta` (date) from each item.
  const isArticleCard = cardType === "article";
  // Data-first cards: hero number + secondary metrics, driven by
  // fieldMapping.stats[]. For records whose title is just a node id
  // ("11623") the number IS the headline.
  const isStatCard = cardType === "stat";
  // Spec-rich shop product cards (image, ref/brand, price, consigne, stock badge, garantie).
  const isProductCard = cardType === "product";

  // Entrance stagger ONLY on the first render (page load): re-filters and
  // pagination must feel instant, so subsequent renders skip the animation.
  const firstRenderRef = useRef(true);
  const isFirstRender = firstRenderRef.current;
  useEffect(() => {
    firstRenderRef.current = false;
  }, []);
  const staggerStyle = (idx: number) =>
    isFirstRender ? { animation: "apa-rise .5s ease-out both", animationDelay: `${Math.min(idx, 8) * 60}ms` } : undefined;

  // ----- Draft form state. Seeded from URL on every payload arrival so a
  // navigation away and back reflects the URL accurately. -----
  const [draftQuery, setDraftQuery] = useState(appliedQuery);
  const [draftTaxonomy, setDraftTaxonomy] = useState(appliedTaxonomy);
  const [draftCategory, setDraftCategory] = useState(appliedCategory);
  const [draftYear, setDraftYear] = useState(appliedYear);
  const [draftSort, setDraftSort] = useState<SortKey>(appliedSort);

  useEffect(() => setDraftQuery(appliedQuery), [appliedQuery]);
  useEffect(() => setDraftTaxonomy(appliedTaxonomy), [appliedTaxonomy]);
  useEffect(() => setDraftCategory(appliedCategory), [appliedCategory]);
  useEffect(() => setDraftYear(appliedYear), [appliedYear]);
  useEffect(() => setDraftSort(appliedSort), [appliedSort]);

  // Auto-submit the form when a select changes — keeps the click-through
  // behaviour the user expects from a filter dropdown without forcing a
  // second click on Filtrer. The search input still requires Enter or the
  // Filtrer button so the user can type without bouncing on every key.
  const formRef = useRef<HTMLFormElement>(null);
  const submitForm = () => {
    formRef.current?.requestSubmit();
  };

  // Pagination metadata from the server. `safeTotalPages` falls back to a
  // derivation of itemsCount / itemsPerPage when the helper isn't wired
  // (older non-Next.js consumers of the SDK).
  const serverTotalPages =
    typeof pagination.totalPages === "number" && pagination.totalPages > 0
      ? pagination.totalPages
      : 0;
  const serverTotalItems =
    typeof pagination.totalItems === "number" ? pagination.totalItems : items.length;
  const safeTotalPages = serverTotalPages > 0
    ? serverTotalPages
    : itemsPerPage > 0
      ? Math.max(1, Math.ceil(items.length / itemsPerPage))
      : 1;
  const safePage =
    typeof pagination.page === "number" && pagination.page > 0
      ? Math.min(Math.max(1, pagination.page), safeTotalPages)
      : 1;
  const canPrev = safePage > 1;
  const canNext = safePage < safeTotalPages;
  const totalItems = serverTotalItems;

  // Build a URL string with the current applied filters + a target page.
  // Used for prev/next pagination so jumping pages preserves filters.
  const buildHref = (overrides: {
    page?: number;
    q?: string;
    sort?: string;
    year?: string;
    taxonomy?: string;
    category?: string;
  } = {}): string => {
    const params = new URLSearchParams();
    const q = (overrides.q ?? appliedQuery).trim();
    const sort = (overrides.sort ?? (appliedSort === "date-desc" ? "" : appliedSort)).trim();
    const year = (overrides.year ?? appliedYear).trim();
    const taxonomy = (overrides.taxonomy ?? appliedTaxonomy).trim();
    const category = (overrides.category ?? appliedCategory).trim();
    const page = overrides.page ?? safePage;
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    if (year) params.set("year", year);
    if (enableTaxonomyFilter && taxonomy) params.set("taxonomy", taxonomy);
    if (enableCategoryFilter && category) params.set("category", category);
    if (page > 1) params.set("page", String(page));
    const qs = params.toString();
    return qs ? `?${qs}` : "?";
  };

  // Active-filter chips reflect the SERVER state, not draft — they're the
  // filters actually in effect. Clearing a chip navigates to a URL without
  // that single param.
  const activeChips: { key: string; label: string; href: string }[] = [];
  if (appliedQuery) {
    activeChips.push({
      key: "q",
      label: `${t(locale, "searchPageTitle")} : « ${appliedQuery} »`,
      href: buildHref({ q: "", page: 1 }),
    });
  }
  if (appliedYear) {
    activeChips.push({
      key: "year",
      label: `${t(locale, "yearLabel")} : ${appliedYear}`,
      href: buildHref({ year: "", page: 1 }),
    });
  }
  if (appliedSort && appliedSort !== "date-desc") {
    const sortOption = SORT_OPTIONS.find((o) => o.value === appliedSort);
    const label = sortOption ? t(locale, sortOption.labelKey) : appliedSort;
    activeChips.push({
      key: "sort",
      label: `${t(locale, "sortLabel")} : ${label}`,
      href: buildHref({ sort: "", page: 1 }),
    });
  }
  if (enableTaxonomyFilter && appliedTaxonomy) {
    const opt = taxonomies.find((t) => (t.key ?? t.id) === appliedTaxonomy);
    activeChips.push({
      key: "taxonomy",
      label: `${t(locale, "taxonomyLabel")} : ${opt?.label ?? appliedTaxonomy}`,
      href: buildHref({ taxonomy: "", page: 1 }),
    });
  }
  if (enableCategoryFilter && appliedCategory) {
    const opt = categories.find((c) => (c.key ?? c.id) === appliedCategory);
    activeChips.push({
      key: "category",
      label: `${t(locale, "category")} : ${opt?.label ?? appliedCategory}`,
      href: buildHref({ category: "", page: 1 }),
    });
  }
  const clearAllHref = "?";

  return (
    // No max-width / horizontal padding here: this grid is always rendered
    // inside the page's constrained `.apa-page-main` container, which already
    // supplies the page gutter and max width.
    <section>
      {/* Optional related-pages cross-nav block. Rendered FIRST (before
          the search/filter bar) so the visitor sees the peer pages at
          the top of the index, then drops into the search controls and
          the results listing below. */}
      {content.relatedLinks ? (
        <div className="mb-6">
          <LinkTileGridRenderer
            component={{
              componentType: "LinkTileGrid",
              key: `${component.key}-related`,
              content: content.relatedLinks,
            }}
          />
        </div>
      ) : null}

      {showControls ? (
        <>
          <FilterForm
            formRef={formRef}
            searchPlaceholder={searchPlaceholder}
            draftQuery={draftQuery}
            setDraftQuery={setDraftQuery}
            draftSort={draftSort}
            setDraftSort={setDraftSort}
            draftYear={draftYear}
            setDraftYear={setDraftYear}
            availableYears={availableYears}
            enableTaxonomyFilter={enableTaxonomyFilter}
            taxonomies={taxonomies}
            draftTaxonomy={draftTaxonomy}
            setDraftTaxonomy={setDraftTaxonomy}
            enableCategoryFilter={enableCategoryFilter}
            categories={categories}
            draftCategory={draftCategory}
            setDraftCategory={setDraftCategory}
            onSelectChange={submitForm}
          />

          {/* Single horizontal separator between the search bar and the
              results listing. */}
          <hr className="mt-6 mb-6 border-t border-slate-200" />
        </>
      ) : null}

      <ResultsHeader
        totalItems={totalItems}
        activeChips={activeChips}
        clearAllHref={clearAllHref}
        hasActiveFilters={activeChips.length > 0}
        // Count lives at the very bottom of the page (left of the centred
        // pagination on desktop, centred on mobile) — never at the top of
        // the listing. This is the same treatment on every paginated index
        // page in the site, not just the ones carrying a cross-nav panel.
        showCount={false}
      />

      {items.length > 0 ? (
        isProductCard ? (
          <div className="grid grid-cols-2 items-stretch gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {items.map((item, idx) => {
              const key = `${resolveLinkHref(item.href) || item.title || "product"}-${idx}`;
              return (
                <div key={key} style={staggerStyle(idx)}>
                  <ProductCard item={item as unknown as ProductCardItem} />
                </div>
              );
            })}
          </div>
        ) : isStatCard ? (
          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item, idx) => {
              const key = `${resolveLinkHref(item.href) || item.title || "stat"}-${idx}`;
              return (
                <StatCard
                  key={key}
                  // Same `meta` field the document card uses for its date
                  // line — keeps the projector config simple (one mapping
                  // for the date no matter which card style you pick).
                  date={item.meta ?? ""}
                  source={item.source}
                  stats={item.stats ?? []}
                  href={item.href}
                />
              );
            })}
          </div>
        ) : isDocumentCard ? (
          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item, idx) => {
              const key = `${resolveLinkHref(item.href) || item.title || "document"}-${idx}`;
              return <DocumentCard key={key} item={item} />;
            })}
          </div>
        ) : isArticleCard ? (
          <div className="grid items-stretch gap-4 sm:gap-6 md:grid-cols-3">
            {items.map((item, idx) => {
              const key = `${resolveLinkHref(item.href) || item.title || "article"}-${idx}`;
              return (
                <div key={key} style={staggerStyle(idx)}>
                <ArticleCardCompact
                  // ArticleItem expects category, title, excerpt, author, authorRole, date, href, imageSrc.
                  // The paginated-grid item carries the equivalents under
                  // different field names — adapt them here so we can reuse
                  // the same card without forcing the projector into a
                  // different shape.
                  category=""
                  title={item.title}
                  excerpt={item.description ?? ""}
                  author={item.author ?? ""}
                  authorRole=""
                  date={item.meta ?? ""}
                  href={item.href}
                  imageSrc={item.imageSrc}
                />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid items-stretch gap-4 sm:gap-6 md:grid-cols-3">
            {items.map((item, idx) => {
              const key = `${resolveLinkHref(item.href) || item.title || "activity"}-${idx}`;
              return (
                <ActivityCard
                  key={key}
                  title={item.title}
                  description={item.description}
                  href={item.href}
                  imageSrc={item.imageSrc}
                  imageAlt={item.imageAlt}
                />
              );
            })}
          </div>
        )
      ) : (
        <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm xl:text-base text-slate-500">
          {t(locale, "noResultsForFilters")}
        </p>
      )}

      {/* Count goes ABOVE the pagination on every paginated index page —
          left-aligned on desktop, centred on mobile. The pagination below
          stays centred alone. Both are hidden in showcase mode (home rows). */}
      {showControls ? (
        <p className="mt-8 text-center text-sm text-slate-500 sm:text-left">
          <span className="font-semibold text-[#121212]">{totalItems}</span>{" "}
          {t(locale, totalItems === 1 ? "resultSingular" : "resultPlural")}
        </p>
      ) : null}

      {showControls && safeTotalPages > 1 ? (
        <nav className="mt-3 flex items-center justify-center gap-3" aria-label="Pagination">
          <RuntimeLink
            href={buildHref({ page: safePage - 1 })}
            disableClicks={!canPrev}
            aria-disabled={!canPrev}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm xl:text-base font-semibold transition ${
              canPrev
                ? "bg-[#00B862] text-white hover:bg-[#00935A]"
                : "pointer-events-none bg-slate-100 text-slate-400"
            }`}
          >
            {t(locale, "previous")}
          </RuntimeLink>
          <span className="text-sm xl:text-base text-slate-700">
            {safePage} {t(locale, "pageOf")} {safeTotalPages}
          </span>
          <RuntimeLink
            href={buildHref({ page: safePage + 1 })}
            disableClicks={!canNext}
            aria-disabled={!canNext}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm xl:text-base font-semibold transition ${
              canNext
                ? "bg-[#00B862] text-white hover:bg-[#00935A]"
                : "pointer-events-none bg-slate-100 text-slate-400"
            }`}
          >
            {t(locale, "next")}
          </RuntimeLink>
        </nav>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Filter form
//
// A plain `<form method="GET">` that submits to the current URL — Next.js
// handles the navigation as a query-only change to the dynamic `[[...slug]]`
// route, re-running the server helper so the rendered HTML is correct for
// every shareable URL. The selects auto-submit on change for snappy
// affordance; the search input + button waits for an explicit Filtrer so
// the user can type without a roundtrip per keystroke.
// ---------------------------------------------------------------------------
function FilterForm(props: {
  formRef: React.RefObject<HTMLFormElement | null>;
  searchPlaceholder: string;
  draftQuery: string;
  setDraftQuery: (value: string) => void;
  draftSort: SortKey;
  setDraftSort: (value: SortKey) => void;
  draftYear: string;
  setDraftYear: (value: string) => void;
  availableYears: string[];
  enableTaxonomyFilter: boolean;
  taxonomies: { id: string; label: string; key?: string }[];
  draftTaxonomy: string;
  setDraftTaxonomy: (value: string) => void;
  enableCategoryFilter: boolean;
  categories: { id: string; label: string; key?: string }[];
  draftCategory: string;
  setDraftCategory: (value: string) => void;
  onSelectChange: () => void;
}) {
  const {
    formRef,
    searchPlaceholder,
    draftQuery,
    setDraftQuery,
    draftSort,
    setDraftSort,
    draftYear,
    setDraftYear,
    availableYears,
    enableTaxonomyFilter,
    taxonomies,
    draftTaxonomy,
    setDraftTaxonomy,
    enableCategoryFilter,
    categories,
    draftCategory,
    setDraftCategory,
    onSelectChange,
  } = props;

  const locale = useLocale();

  const selectClass =
    "h-10 rounded-lg border border-slate-300 bg-white px-3 pr-8 text-sm text-[#121212] shadow-sm transition " +
    "focus:border-[#00B862] focus:outline-none focus:ring-2 focus:ring-[#00B862]/60 focus:shadow-[0_0_0_4px_rgba(0,184,98,.12)]";

  // Tablet layout uses `sm:grid-cols-2` for the filters cluster — pair the
  // selects 2 per row. When the page exposes an ODD number of selects the
  // last one ends up alone on its row; give it `sm:col-span-2` so it fills
  // the full row instead of leaving an awkward empty slot on the right.
  // The visible-select order below mirrors the JSX order.
  const visibleSelects: ("sort" | "year" | "taxonomy" | "category")[] = ["sort"];
  if (availableYears.length > 0) visibleSelects.push("year");
  if (enableTaxonomyFilter) visibleSelects.push("taxonomy");
  if (enableCategoryFilter) visibleSelects.push("category");
  const oddCount = visibleSelects.length % 2 === 1;
  const lastSelect = visibleSelects[visibleSelects.length - 1];
  const fullRowIfLast = (name: typeof lastSelect) =>
    oddCount && name === lastSelect ? " sm:col-span-2" : "";

  return (
    // Two-mode layout:
    //   <lg (mobile + tablet): everything stacked, every control full width
    //                           — predictable, never overflows, no crushed
    //                           dropdowns regardless of how many selects
    //                           the page exposes.
    //   ≥lg (desktop):           inline on one row (search-left, selects
    //                           + button-right).
    <form
      ref={formRef}
      method="GET"
      role="search"
      className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3"
    >
      {/* Submitting any filter always resets the page to 1 — otherwise a user
          who reaches page 5, then narrows the filter, would land on a
          truncated or empty page 5 of the filtered set. */}
      <input type="hidden" name="page" value="1" />

      {/* Search input. Full width on mobile/tablet (<lg); on lg+ capped
          at ~24rem so the filter cluster on the right keeps room for all
          selects + the button on one line. */}
      <label className="relative block w-full lg:flex-1 lg:max-w-sm">
        <span className="sr-only">{t(locale, "search")}</span>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          name="q"
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          placeholder={searchPlaceholder}
          autoComplete="off"
          className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-9 text-sm text-[#121212] shadow-sm transition placeholder:text-slate-400 focus:border-[#00B862] focus:outline-none focus:ring-2 focus:ring-[#00B862]/60 focus:shadow-[0_0_0_4px_rgba(0,184,98,.12)]"
        />
        {draftQuery ? (
          <button
            type="button"
            aria-label={t(locale, "clearSearch")}
            onClick={() => {
              setDraftQuery("");
              onSelectChange();
            }}
            className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-[#00B862]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </label>

      {/* Filters cluster — 3 layout modes:
            <sm  (mobile):   1 column grid — every control on its own row.
            sm-lg (tablet):  2 column grid — selects pair up 2 per row;
                              the Filtrer button below spans both columns
                              so it sits alone on its own row.
            ≥lg  (desktop):  flex row — search + selects + button on
                              ONE line, selects share width via
                              `flex-1 min-w-0`, button natural width. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-1 lg:flex-row lg:items-center">
        <select
          name="sort"
          aria-label={t(locale, "sortResults")}
          value={draftSort}
          onChange={(event) => {
            setDraftSort(event.target.value as SortKey);
            onSelectChange();
          }}
          className={`${selectClass} w-full lg:w-auto lg:min-w-0 lg:flex-1${fullRowIfLast("sort")}`}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(locale, "sortLabel")} : {t(locale, opt.labelKey)}
            </option>
          ))}
        </select>

        {availableYears.length > 0 ? (
          <select
            name="year"
            aria-label={t(locale, "filterByYear")}
            value={draftYear}
            onChange={(event) => {
              setDraftYear(event.target.value);
              onSelectChange();
            }}
            className={`${selectClass} w-full lg:w-auto lg:min-w-0 lg:flex-1${fullRowIfLast("year")}`}
          >
            <option value="">{t(locale, "yearLabel")} : {t(locale, "allFeminine")}</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {t(locale, "yearLabel")} : {year}
              </option>
            ))}
          </select>
        ) : null}

        {enableTaxonomyFilter ? (
          <select
            name="taxonomy"
            aria-label={t(locale, "filterByTopic")}
            value={draftTaxonomy}
            onChange={(event) => {
              setDraftTaxonomy(event.target.value);
              onSelectChange();
            }}
            className={`${selectClass} w-full lg:w-auto lg:min-w-0 lg:flex-1${fullRowIfLast("taxonomy")}`}
          >
            <option value="">{t(locale, "taxonomyLabel")} : {t(locale, "allFeminine")}</option>
            {taxonomies.map((option) => (
              <option key={option.id} value={option.key ?? option.id}>
                {option.label}
              </option>
            ))}
          </select>
        ) : null}

        {enableCategoryFilter ? (
          <select
            name="category"
            aria-label={t(locale, "filterByCategory")}
            value={draftCategory}
            onChange={(event) => {
              setDraftCategory(event.target.value);
              onSelectChange();
            }}
            className={`${selectClass} w-full lg:w-auto lg:min-w-0 lg:flex-1${fullRowIfLast("category")}`}
          >
            <option value="">{t(locale, "category")} : {t(locale, "allFeminine")}</option>
            {categories.map((option) => (
              <option key={option.id} value={option.key ?? option.id}>
                {translateCategoryPathLabel(locale, option.label, option.key)}
              </option>
            ))}
          </select>
        ) : null}

        <button
          type="submit"
          // <sm:  occupies the single-column row (w-full).
          // sm-lg: spans both grid columns so it sits ALONE on its row
          //        below the 2-per-row selects (`sm:col-span-2`).
          // ≥lg:  inline, natural width on the right (`lg:col-auto`
          //        — col-span is ignored once the grid becomes a flex row).
          className="h-10 w-full shrink-0 inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-[#00B862] px-4 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-px hover:bg-[#00935A] hover:shadow-[var(--shadow-glow)] sm:col-span-2 lg:col-auto lg:w-auto"
        >
          {t(locale, "filterLabel")}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Results header: a thin line that shows how many items matched (from the
// server) and surfaces every active filter as a removable chip. The chips
// are anchors so removing them is a normal SSR navigation — the URL drops
// the param, the server re-renders, the user keeps a shareable URL.
// ---------------------------------------------------------------------------
function ResultsHeader(props: {
  totalItems: number;
  activeChips: { key: string; label: string; href: string }[];
  clearAllHref: string;
  hasActiveFilters: boolean;
  /** Defaults to true. Pass false when the count is rendered separately
   *  (e.g. as a footer below the pagination). */
  showCount?: boolean;
}) {
  const { totalItems, activeChips, clearAllHref, hasActiveFilters, showCount = true } = props;
  const locale = useLocale();
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
      {showCount ? (
        <p>
          <span className="font-semibold text-[#121212]">{totalItems}</span>{" "}
          {t(locale, totalItems === 1 ? "resultSingular" : "resultPlural")}
        </p>
      ) : (
        // Keep an invisible placeholder so the chips still right-align via
        // `justify-between` when the count is hidden.
        <span aria-hidden="true" />
      )}
      {hasActiveFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          {activeChips.map((chip) => (
            <RuntimeLink
              key={chip.key}
              href={chip.href}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--color-forest-100)] px-3 py-1 text-xs font-medium text-[var(--color-forest-700)] ring-1 ring-[#00B862]/20 transition hover:bg-[#d6ecdc] hover:ring-[#00B862]/40"
            >
              {chip.label}
              <X className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">{t(locale, "removeFilter")}</span>
            </RuntimeLink>
          ))}
          <RuntimeLink
            href={clearAllHref}
            className="text-xs font-semibold text-[#00B862] underline-offset-2 hover:underline"
          >
            {t(locale, "clearAllFilters")}
          </RuntimeLink>
        </div>
      ) : null}
    </div>
  );
}

function isSortKey(value: string): value is SortKey {
  return value === "date-desc" || value === "date-asc" || value === "title-asc";
}

// Pull a four-digit year out of an item's meta string. Tolerant of any
// surrounding French formatting ("12 mars 2026", "Publié le 5 février 2025
// à 14:30", "2024-08-12T…"). Returns `undefined` when no year is present.
function extractYear(meta?: string): string | undefined {
  if (!meta) return undefined;
  const match = /\b(20\d{2})\b/.exec(meta);
  return match ? match[1] : undefined;
}
